import { StateCreator } from "zustand";
import { ThreePsStore, ThreePsAsyncActions, IFetchCodesByVendorOpts } from "./threePsTypes";
import threePsCodesApi from "../threePsCodes.api";
import { ISaveThreePsCodePayload, IThreePsCodeListResponse } from "../threePsCodes.model";
import { CommonResponseModel } from "../../../Models/tableDetailsModel";
import { toast } from "react-toastify";
import { getApiErrorMessage } from "../../../utils/getApiErrorMessage";
import { shouldShowErrorToast } from "../../../utils/apiRequestError";

export const createThreePsStoreAsyncActions: StateCreator<ThreePsStore, [], [], ThreePsAsyncActions> = (set, get) => ({
    fetchVendors: async (setShowLoader?: React.Dispatch<React.SetStateAction<boolean>>) => {
        const { vendors, isVendorLoading } = get();
        if (vendors.length > 0 || isVendorLoading) return;

        try {
            set({ isVendorLoading: true, vendorsError: null });
            setShowLoader?.(true);
            const response:CommonResponseModel<string[]> = await threePsCodesApi.GetThreePsVendorList.get();

            if(response.flagStatus) {
                const firstVendor = response.value?.[0] ?? null;
                const nextSelectedVendor = get().selectedVendor ?? firstVendor;
    
                set(() => ({
                    vendors: response.value,
                    selectedVendor: nextSelectedVendor,
                }));

                if(nextSelectedVendor) {
                    await get().fetchCodesByVendor(nextSelectedVendor, {
                        setShowLoader: setShowLoader,
                    } );
                }
            } else {
                toast.error(response.error ?? 'Failed to fetch vendors');
            }

        } catch (error) {
            const message = getApiErrorMessage(error, "Failed to fetch vendors");
            set({ vendorsError: message, vendors: [] });
            if (shouldShowErrorToast(error)) {
                toast.error(message);
            }
        } finally {
            set({ isVendorLoading: false });
            setShowLoader?.(false);
        }
    },

    fetchCodesByVendor: async (vendor: string, opts?: IFetchCodesByVendorOpts) => {
        const { force = false, setShowLoader } = opts ?? {};
        const state = get();
        const pageIndex = opts?.pageIndex ?? state.pageIndex;
        const pageSize = opts?.pageSize ?? state.pageSize;
        const { codesByVendor, codesPageMetaByVendor, isCodesLoading } = state;

        // Skip API call only when cache matches requested vendor + pagination.
        const cachedCodes = codesByVendor[vendor];
        const cachedPageMeta = codesPageMetaByVendor[vendor];
        const isCachedForRequestedPage = Array.isArray(cachedCodes)
            && cachedCodes.length > 0
            && cachedPageMeta?.pageIndex === pageIndex
            && cachedPageMeta?.pageSize === pageSize;

        if (!force && isCachedForRequestedPage && cachedPageMeta) {
            // Rehydrate global pagination UI from per-vendor cache (esp. total).
            set({
                totalThreePsCodes: cachedPageMeta.total,
                pageIndex: cachedPageMeta.pageIndex,
                pageSize: cachedPageMeta.pageSize,
            });
            return;
        }

        if (isCodesLoading) return;

        try {
            set({ isCodesLoading: true, codesError: null });
            setShowLoader?.(true);
            const response: CommonResponseModel<IThreePsCodeListResponse> = await threePsCodesApi.GetThreePsCodeList.post({ vendor, pageSize, pageIndex });
            if(response.flagStatus) {
                const total = response.value?.totalThreePsCodes ?? 0;
                set((state) => ({
                    codesByVendor: { ...state.codesByVendor, [vendor]: response.value?.codeDetails ?? [] },  
                    codesPageMetaByVendor: { ...state.codesPageMetaByVendor, [vendor]: { pageIndex, pageSize, total } },
                    totalThreePsCodes: total,
                }));
            } else {
                toast.error(response.error ?? 'Failed to fetch three ps code list');
            }
        } catch (error) {
            console.error("Error fetching three ps code list:", error);
            const message = getApiErrorMessage(error, "Failed to fetch three ps code list");
            if (shouldShowErrorToast(error)) {
                toast.error(message);
            }
            set((state) => ({
                codesError: message,
                codesByVendor: { ...state.codesByVendor, [vendor]: [] },
                codesPageMetaByVendor: { ...state.codesPageMetaByVendor, [vendor]: { pageIndex, pageSize, total: 0 } },
                totalThreePsCodes: 0,
            }));
        } finally {
            set({ isCodesLoading: false });
            setShowLoader?.(false);
        }
    },

    saveThreePsCode: async (payload: ISaveThreePsCodePayload[], opts?: { setShowLoader?: React.Dispatch<React.SetStateAction<boolean>> }) => {
        const { setShowLoader } = opts ?? {};
        const { selectedVendor, fetchCodesByVendor } = get();
        try {
            set({ isSaveLoading: true, saveError: null });
            setShowLoader?.(true);
            const response: CommonResponseModel<string> = await threePsCodesApi.SaveThreePsCode.post(payload);
            if(response.flagStatus && selectedVendor) {
                await fetchCodesByVendor(selectedVendor, {
                    force: true,
                    setShowLoader,
                });
            } else {
                toast.error(response.error ?? 'Failed to save three ps code');
            }
        } catch (error) {
            const message = getApiErrorMessage(error, "Failed to save three ps code");
            set(() => ({
                saveError: message,
            }));
            if (shouldShowErrorToast(error)) {
                toast.error(message);
            }
        } finally {
            set({ isSaveLoading: false });
            setShowLoader?.(false);
        }
    }
})
